/* eslint-disable max-statements, max-lines-per-function */

import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  ContentAuthor,
  News,
  NewsUpdateInput
} from "@bluframe/mightychroma-mightytool-frontend-types"
import slugify from "@sindresorhus/slugify"
import { Props as ImageSelectorAndCarouselProps } from "components/ImageSelectorAndCarousel"
import { Props as SelectProps } from "components/inputs/Select"
import { FormEventHandler, RefObject, useMemo, useRef, useState } from "react"

import Item from "./Item"

type ModalContent =
  | "deleteConfirm"
  | "generateConfirm"
  | "publishConfirm"
  | null

export interface Props {
  isAdd?: boolean
  isAdmin?: boolean
  contentAuthors?: ContentAuthor[]
  isEditing?: boolean
  isEditor?: boolean
  isContentAuthorsLoading?: boolean
  isGenerateLoading: boolean
  isPublishLoading: boolean
  isUpdateLoading: boolean
  item: News
  hasLinkedHomeVideo: boolean
  onCancel: () => void
  onDeleteModalClose: () => void
  onDelete: () => void
  onEdit: () => void
  onAuthorChange?: SelectProps["onChange"]

  onGenerate: (additionalInstructions?: string) => void
  onPublishToggle: () => void

  onSave: (contest: Omit<NewsUpdateInput, "id">) => void
  onToggleCascadeDelete: () => void
  shouldCascadeDelete: boolean
}

export interface ComponentProps extends Omit<
  Props,
  | "isEditing"
  | "onCancel"
  | "onDelete"
  | "onEdit"
  | "onGenerate"
  | "onPublishToggle"
  | "onSave"
> {
  authorSelectValue?: SelectProps["value"]
  actionButtonLabel: string
  actionButtonOnClick?: () => void
  additionalInstructionsRef: RefObject<HTMLTextAreaElement | null>
  areActionsDisabled: boolean
  isEditMode: boolean
  isModalOpen: boolean
  modalContent: ModalContent
  nameRef: RefObject<HTMLInputElement | null>
  onAuthorChange?: SelectProps["onChange"]
  onGenerateSlug: () => void
  onModalClose: () => void

  onModalConfirmOpen: (modalContent: ModalContent) => () => void
  onModalDeleteConfirm: () => void
  onModalGenerateConfirm: () => void
  onModalPublishToggleConfirm: () => void
  onSetSelectedImages: ImageSelectorAndCarouselProps["onSetSelectedImages"]
  onSubmit: FormEventHandler
  outlinedButtonLabel: string
  outlinedButtonOnClick?: () => void
  selectedImages: ImageSelectorAndCarouselProps["selectedImages"]
  slugRef: RefObject<HTMLInputElement | null>
  subheadingRef: RefObject<HTMLTextAreaElement | null>
  textRef: RefObject<HTMLTextAreaElement | null>
}

const DEFAULT_MODAL_CONTENT = null

export const usePrepareComponent = ({
  isEditing,
  onCancel,
  onDelete,
  onEdit,
  onGenerate,
  onPublishToggle,
  onSave,
  ...props
}: Props): ComponentProps => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalContent, setModalContent] = useState<ModalContent>(
    DEFAULT_MODAL_CONTENT
  )
  const [selectedImages, setSelectedImages] = useState<
    ImageSelectorAndCarouselProps["selectedImages"]
  >(
    () =>
      props.item.images?.map((item) => ({
        contentId: item.id ? Number(item.id) : undefined,
        filename: item.filename ?? item.url,
        originalUrl: item.url
      })) ?? []
  )

  const additionalInstructionsRef = useRef<HTMLTextAreaElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)
  const slugRef = useRef<HTMLInputElement>(null)
  const subheadingRef = useRef<HTMLTextAreaElement>(null)
  const textRef = useRef<HTMLTextAreaElement>(null)

  const getAuthorIdValue = (value?: string | number | null): number | null => {
    if (value === null || value === undefined) {
      return null
    }

    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
  }

  const isEditMode = Boolean(props.isAdd || isEditing)
  const authorIdFromItem = useMemo(
    () => getAuthorIdValue(props.item?.author?.id),
    [props.item?.author?.id]
  )
  const [authorSelectValue, setAuthorSelectValue] = useState<number | null>(
    authorIdFromItem
  )

  const onAuthorChange: ComponentProps["onAuthorChange"] = (value) => {
    setAuthorSelectValue(getAuthorIdValue(value as string | number | null))
  }

  const onModalClose = () => {
    if (modalContent === "deleteConfirm") {
      props.onDeleteModalClose()
    }

    setModalContent(DEFAULT_MODAL_CONTENT)
    setIsModalOpen(false)
  }

  const onModalConfirmOpen = (newModalContent: ModalContent) => () => {
    setModalContent(newModalContent)
    setIsModalOpen(true)
  }

  const onModalDeleteConfirm = () => {
    onDelete()
    onModalClose()
  }

  const actionButtonLabel = isEditMode ? "Save" : "Delete"
  const outlinedButtonLabel = isEditMode ? "Cancel" : "Edit"

  const actionButtonOnClick = isEditMode
    ? undefined
    : onModalConfirmOpen("deleteConfirm")
  const outlinedButtonOnClick = isEditMode
    ? () => {
        setAuthorSelectValue(authorIdFromItem)
        onCancel()
      }
    : () => {
        setAuthorSelectValue(authorIdFromItem)
        onEdit()
      }

  const onSubmit: FormEventHandler = (event) => {
    event.preventDefault()

    if (!nameRef.current) {
      return
    }

    onSave({
      authorId: authorSelectValue ?? undefined,
      images: selectedImages.map((item) => ({
        contentId: item.contentId ? item.contentId : undefined,
        originalUrl: item.originalUrl
      })),
      name: nameRef.current.value ?? "",
      slug: slugRef.current?.value ?? "",
      subheading: subheadingRef.current?.value,
      text: textRef.current?.value ?? ""
    })
  }

  const onModalGenerateConfirm = () => {
    onGenerate(
      additionalInstructionsRef.current?.value === ""
        ? undefined
        : additionalInstructionsRef.current?.value
    )
    onModalClose()
  }

  const onModalPublishToggleConfirm = () => {
    onPublishToggle()
    onModalClose()
  }

  const onGenerateSlug = () => {
    if (!nameRef.current || !slugRef.current) {
      return
    }
    slugRef.current.value = slugify(nameRef.current.value)
  }

  const areActionsDisabled =
    props.isGenerateLoading || props.isPublishLoading || props.isUpdateLoading

  const onSetSelectedImages: ImageSelectorAndCarouselProps["onSetSelectedImages"] =
    (newSelectedImages) => {
      setSelectedImages(newSelectedImages)
    }

  return {
    ...props,
    actionButtonLabel,
    actionButtonOnClick,
    authorSelectValue,
    additionalInstructionsRef,
    areActionsDisabled,
    isEditMode,
    isModalOpen,
    modalContent,
    nameRef,
    onAuthorChange,
    onGenerateSlug,
    onModalClose,
    onModalConfirmOpen,
    onModalDeleteConfirm,
    onModalGenerateConfirm,
    onModalPublishToggleConfirm,
    onSetSelectedImages,
    onSubmit,
    outlinedButtonLabel,
    outlinedButtonOnClick,
    selectedImages,
    slugRef,
    subheadingRef,
    textRef
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  Item
)
